import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { useEffect, useState } from "react";
import { emulatorGoogleIdToken } from "../../lib/auth-token";
import { useClients } from "../../lib/clients";

export type SessionState = { status: "loading" } | { status: "signed_out" } | { status: "signed_in"; user: User };

/** The Firebase session, restored from the browser on load. */
export function useSession(): SessionState {
  const { firebase } = useClients();
  const [state, setState] = useState<SessionState>({ status: "loading" });
  useEffect(
    () =>
      onAuthStateChanged(firebase.auth, (user) =>
        setState(user === null ? { status: "signed_out" } : { status: "signed_in", user }),
      ),
    [firebase.auth],
  );
  return state;
}

export function useSignIn() {
  const { firebase } = useClients();
  return async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    await signInWithPopup(firebase.auth, provider);
  };
}

/** Null unless the runtime config points at the emulators (never in staging or prod). */
export function useEmulatorSignIn(): ((email: string, name: string) => Promise<void>) | null {
  const { config, firebase } = useClients();
  if (!config.useEmulators) return null;
  return async (email, name) => {
    await signInWithCredential(firebase.auth, GoogleAuthProvider.credential(emulatorGoogleIdToken(email, name)));
  };
}

export function useSignOut() {
  const { firebase } = useClients();
  return () => signOut(firebase.auth);
}
